import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Area } from '../models/area';
import { Organization } from '../models/organization';
import { AreaService } from './area.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class AppInstanceService {
  private appInstanceRoute = 'items/app_instance/';
  public appInstanceSettings: AppInstance;

  constructor(
    private userService: UserService,
    private areaService: AreaService,
    private http: HttpClient,
  ) {}

  public async getAppInstanceSettings() {
    const url = new URL(location.href);
    const appInstanceId = environment.appInstanceId;
    let domain = '';
    let isAzurewebsites = url.hostname.split('.')[1] === 'azurewebsites';
    if (url.hostname === 'localhost') {
      domain = appInstanceId;
    } else if (isAzurewebsites) {
      const azureWebsitesHostName = url.hostname.split('.')[0];
      domain = azureWebsitesHostName.split('-')[0];
    } else {
      domain = url.hostname.split('.')[1];
    }
    const appSettings = await this.getSettings(domain);
    this.userService.setAppInstance(appSettings);
  }

  public async storeOrganizationAndArea(organization: Organization) {
    // Stores organization details
    this.userService.setUserOrganization(organization);

    // Stores area ids
    const hierarchy = await this.areaService.getHierarchy(organization.area.id);
    const hierarchyList = this.transformHierarchyToList(hierarchy);
    this.userService.setArea(organization.area.id, hierarchyList);
  }

  private transformHierarchyToList(obj: Area[]) {
    const hierarchyList: string[] = [];
    recursively(obj);
    function recursively(obj: Area[] | Area) {
      for (var k in obj) {
        if (typeof obj[k] == 'object' && obj[k] !== null) {
          recursively(obj[k]);
        } else {
          hierarchyList.push(obj[k]);
        }
      }
    }
    return hierarchyList;
  }

  private async getSettings(appInstanceName: string) {
    const fields = '*,translations.*,areas.area_id';
    const url = new URL(`${environment.directusBaseUrl}${this.appInstanceRoute}${appInstanceName}`);
    url.searchParams.set('fields', fields);
    const response = await this.http.get<{ data: AppInstance }>(url.href).toPromise();
    return response.data;
  }

  public updateAppTheme() {
    this.userService.restoreUserFromLocalStorage().then((user) => {
      const primaryColor = user.appInstance && user.appInstance.primary_color;
      const secondaryColor = user.appInstance && user.appInstance.secondary_color;
      const tertiaryColor = user.appInstance && user.appInstance.tertiary_color;
      if (primaryColor) {
        document.documentElement.style.setProperty('--ion-color-primary', primaryColor);
      }
      if (secondaryColor) {
        document.documentElement.style.setProperty('--ion-color-secondary', secondaryColor);
      }
      if (tertiaryColor) {
        document.documentElement.style.setProperty('--ion-color-tertiary', tertiaryColor);
      } else if (secondaryColor) {
        // Fallback to secondary color
        document.documentElement.style.setProperty('--ion-color-tertiary', secondaryColor);
      }
    });
  }
}

export interface AppInstance {
  id: string;
  translations: {
    languages_code: string;
    name: string;
    greetings: string;
  }[];
  logo: string;
  splash: string;
  primary_color: string;
  secondary_color: string;
  tertiary_color: string;
  areas: {
    area_id: string;
  }[];
  platform: number;
}
